import {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

/**
 * useInlineEdit — story-local click-to-edit state machine for StackedCard
 * (task 4/8). Presentation-only; not exported from the design-system barrel.
 *
 * A card in preview/placeholder mode enters an EDITING state on click: a
 * card-centered text field with confirm/cancel keyboard rules, an optional
 * validator, and a single-line ↔ multi-line mode switch. The hook owns only the
 * editing lifecycle (mode, working value, error, keyboard + outside-click); the
 * card chrome and the async handoff after commit (e.g. the link unfurl ladder
 * from task 3/8) live in the story that consumes it.
 *
 * ── The decided rules (AC task #454744) ──
 *  Single-line   Enter confirms · click-outside confirms · Esc cancels + reverts
 *  Multi-line    Enter = newline · Cmd/Ctrl+Enter saves · click-outside saves ·
 *                Esc cancels + reverts
 *  Validator     validate(value) => true | errorMessage, run before every accept
 *                pass  → commit + exit editing
 *                fail via a key   → STAY editing, surface the message (retry)
 *                fail via outside → REVERT (acts as cancel) — user never trapped
 *
 * Keyboard is handled on the field's own keydown (never a global listener) and
 * every keydown stops propagation, so the card focus model (task 2/8) can't
 * swallow the Enter/Esc that belong to the editor.
 */

export type InlineEditValidator = (value: string) => true | string;

export interface UseInlineEditConfig {
  /** Seed value for the first edit; also the revert target until first commit. */
  initialValue?: string;
  /** Multi-line mode: Enter inserts a newline, Cmd/Ctrl+Enter saves. */
  multiline?: boolean;
  /** Optional validator, run before every accept. `true` passes; a string fails. */
  validate?: InlineEditValidator;
  /** Called with the committed value when an accept passes validation. */
  onCommit?: (value: string) => void;
}

/** Props to spread onto the `<input>` or `<textarea>` while editing. */
export interface InlineEditBind {
  /** Callback ref — assignable to both an input and a textarea. */
  ref: (el: HTMLInputElement | HTMLTextAreaElement | null) => void;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  "aria-invalid": boolean;
}

export interface InlineEditController {
  /** Whether the field is currently shown. */
  editing: boolean;
  /** The working value (the draft being typed). */
  value: string;
  /** The last committed value — what a revert restores. */
  committed: string;
  /** The current validation message, or null when valid / untouched. */
  error: string | null;
  /** Whether this editor is in multi-line mode. */
  multiline: boolean;
  /** Enter editing, loading the last committed value. */
  begin: () => void;
  /** Cancel + revert to the committed value (the Esc / invalid-outside path). */
  cancel: () => void;
  /** Force a committed value without going through the field (repeat demos). */
  reset: (next?: string) => void;
  /** Spread onto the field element while `editing`. */
  bind: InlineEditBind;
}

export const useInlineEdit = ({
  initialValue = "",
  multiline = false,
  validate,
  onCommit,
}: UseInlineEditConfig = {}): InlineEditController => {
  const [editing, setEditingState] = useState(false);
  const [value, setValueState] = useState(initialValue);
  const [committed, setCommittedState] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);

  // Refs mirror the state that the (stable) event handlers read, so the handlers
  // never go stale and never need to be re-created as the value changes.
  const elRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const valueRef = useRef(value);
  const committedRef = useRef(committed);
  const editingRef = useRef(editing);
  // Set right before a programmatic exit so the blur that follows the field
  // unmounting is ignored (it isn't a real click-outside).
  const skipBlurRef = useRef(false);
  const validateRef = useRef(validate);
  const onCommitRef = useRef(onCommit);

  useEffect(() => {
    validateRef.current = validate;
    onCommitRef.current = onCommit;
  });

  const setValue = useCallback((v: string) => {
    valueRef.current = v;
    setValueState(v);
  }, []);

  const setCommitted = useCallback((v: string) => {
    committedRef.current = v;
    setCommittedState(v);
  }, []);

  const setEditing = useCallback((v: boolean) => {
    editingRef.current = v;
    setEditingState(v);
  }, []);

  const begin = useCallback(() => {
    skipBlurRef.current = false;
    setValue(committedRef.current);
    setError(null);
    setEditing(true);
  }, [setValue, setEditing]);

  const cancel = useCallback(() => {
    skipBlurRef.current = true;
    setValue(committedRef.current);
    setError(null);
    setEditing(false);
  }, [setValue, setEditing]);

  const reset = useCallback(
    (next: string = initialValue) => {
      skipBlurRef.current = true;
      setCommitted(next);
      setValue(next);
      setError(null);
      setEditing(false);
    },
    [initialValue, setCommitted, setValue, setEditing]
  );

  const commit = useCallback(
    (v: string) => {
      skipBlurRef.current = true;
      setCommitted(v);
      setValue(v);
      setError(null);
      setEditing(false);
      onCommitRef.current?.(v);
    },
    [setCommitted, setValue, setEditing]
  );

  // Run the validator (if any) and accept, or handle the failure per source:
  // a key retry keeps the user in the field with a message; an outside click
  // reverts so the user is never trapped in an invalid editor.
  const attempt = useCallback(
    (source: "key" | "outside"): boolean => {
      const v = valueRef.current;
      const result = validateRef.current ? validateRef.current(v) : true;
      if (result === true) {
        commit(v);
        return true;
      }
      if (source === "outside") {
        cancel();
        return false;
      }
      setError(result);
      return false;
    },
    [commit, cancel]
  );

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValue(e.target.value);
      // Clear a stale error as the user edits; the next accept re-validates.
      setError((prev) => (prev == null ? prev : null));
    },
    [setValue]
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Keep Enter/Esc from reaching the card's own keyboard model (task 2/8).
      e.stopPropagation();

      if (e.key === "Escape") {
        e.preventDefault();
        cancel();
        return;
      }

      if (e.key === "Enter") {
        const withMeta = e.metaKey || e.ctrlKey;
        // Multi-line: a bare Enter is a newline; only Cmd/Ctrl+Enter saves.
        if (multiline && !withMeta) return;
        e.preventDefault();
        attempt("key");
      }
    },
    [attempt, cancel, multiline]
  );

  const onBlur = useCallback(() => {
    if (skipBlurRef.current) {
      skipBlurRef.current = false;
      return;
    }
    if (!editingRef.current) return;
    attempt("outside");
  }, [attempt]);

  const setRef = useCallback(
    (el: HTMLInputElement | HTMLTextAreaElement | null) => {
      elRef.current = el;
    },
    []
  );

  // On entering editing, focus the field and drop the caret at the end so the
  // cursor is visible and existing text is ready to extend.
  useEffect(() => {
    if (!editing) return;
    const el = elRef.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    try {
      el.setSelectionRange(len, len);
    } catch {
      /* some input types disallow setSelectionRange — harmless here. */
    }
  }, [editing]);

  return {
    editing,
    value,
    committed,
    error,
    multiline,
    begin,
    cancel,
    reset,
    bind: {
      ref: setRef,
      value,
      onChange,
      onKeyDown,
      onBlur,
      "aria-invalid": error != null,
    },
  };
};
